import React, { useEffect, useState } from 'react';
import './atlas-responsible-perspectives.scss';
import CheckboxIcon from '../CheckboxIcon/CheckboxIcon';
import { Tooltip } from 'primereact/tooltip';
import { PermissionList } from '../../../../types/sharedTypes'; // Import PermissionList

interface DropdownProps {
  value: any[];
  options: any[]; // Options for checkboxes
  onChange: (e: { value: any[] }) => void;
  optionLabel?: string;
  className?: string;
  disabled?: boolean;
  permissionList: PermissionList; // Add permissionList prop
  onPerspectiveSelect?: (option: any) => void;
  setSelectedPerspectiveSystemName: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  handleAssignPerspective?: (option: any) => void;
  selectedParticipant: any;
}

const AtlasResponsiblePerspectives: React.FC<DropdownProps> = ({
  value = [],
  options,
  onChange,
  optionLabel = 'label',
  className = '',
  disabled = false,
  permissionList,
  onPerspectiveSelect,
  setSelectedPerspectiveSystemName,
  handleAssignPerspective,
  selectedParticipant,
}) => {
  // --- Permission Logic ---
  const { view, manage_security } = permissionList.permissions;

  const [selectedOptions, setSelectedOptions] = useState<any[]>(value); // State for selected checkboxes

  const [rotate, setRotate] = useState(false);

  useEffect(() => {
    setSelectedOptions(value);
    setRotate(true); // Trigger rotation on change
    setTimeout(() => setRotate(false), 1000); // Reset rotation after animation
  }, [value]);

  // If the user doesn't have view permissions, do not render anything
  if (!view) {
    return null;
  }

  // If user can view but cannot edit, the component should be read-only
  const isReadOnly = view && !manage_security;
  const tooltipKey = JSON.stringify(selectedOptions);

  // Handle changes when an option is clicked
  const handleOptionChange = async (optionValue: any) => {
    if (isReadOnly) return;

    // Update the 'selected' property of the option
    const updatedOptions = selectedOptions.map((opt) =>
      opt.value === optionValue.value && !opt.disabled
        ? { ...opt, selected: !opt.selected }
        : opt
    );

    setSelectedOptions(updatedOptions);
    onChange({ value: updatedOptions });

    // Get correct option from updated list
    const updatedOption = updatedOptions.find(
      (opt) => opt.value === optionValue.value
    );
    handleAssignPerspective?.(updatedOption);
  };

  return (
    <div className={`responsible-perspectives-container ${className}`}>
      <Tooltip
        key={tooltipKey}
        target=".info-circle"
        position="right"
        className="custom-tooltip"
        showDelay={1000}
      />
      <div className="header-container">
        <label htmlFor="perspectives-header" className="header">
          Perspectives
        </label>
        <span className={`icon ${rotate ? 'rotate' : ''}`}>
          <i className="lock-rotation mr-2"></i>
        </span>
      </div>

      <div
        id="perspectives-header"
        className="atlas-responsible-perspectives flex flex-column gap-0"
      >
        {options.map((option) => {
          // Find the corresponding option from the updated list if necessary
          const currentOption =
            selectedOptions.find((opt) => opt.value === option.value) || option;
          const isSelected = currentOption.selected;
          const isDisabled = option.label === 'Responsible';

          return (
            <button
              key={option.value}
              type="button"
              className={`flex align-items-center ${isSelected ? 'selected' : ''} ${
                isDisabled ? 'disabled' : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (onPerspectiveSelect) {
                  onPerspectiveSelect(option);
                  setSelectedPerspectiveSystemName(option.systemName);
                }
              }}
              style={{ cursor: isReadOnly ? 'not-allowed' : 'pointer' }}
              disabled={isReadOnly || isDisabled}
            >
              <div className="perspectives-list">
                <button
                  type="button"
                  className="custom-checkbox"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isReadOnly && !isDisabled && selectedParticipant) {
                      handleOptionChange(option);
                    }
                  }}
                  disabled={isReadOnly || isDisabled || !selectedParticipant}
                  aria-label={`Toggle ${option[optionLabel]}`}
                >
                  <CheckboxIcon isSelected={isSelected} />
                </button>
                <label htmlFor={option.value} className="ml-2">
                  {option[optionLabel]}
                </label>
              </div>
              {isSelected && (
                <span
                  className="info-circle ml-auto"
                  data-pr-tooltip={
                    option.description || 'No description available'
                  }
                ></span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AtlasResponsiblePerspectives;
